import { useEffect, useRef, useState } from 'react';
import type { StreamEvent } from '@hypevault/shared-types';

/**
 * Supporters — v2 port of v1's supporters.ejs.
 * Tracks viewer contributions (tips, bits, super chats) over the session
 * and shows top N supporters with their totals.
 *
 * Config (all optional):
 *   maxSupporters: number       (default 5)
 *   accentColor: string         (default '#fbbf24')
 *   title: string               (default 'Top Supporters')
 *   metric: 'coins' | 'usd'     (default 'coins') — display unit
 *   countCheers: boolean        (default true — include twitch bits)
 *   countSuperChats: boolean    (default true)
 *   countTips: boolean          (default true)
 *   showRank: boolean           (default true)
 *   compact: boolean            (default false)
 */

interface Supporter {
  name: string;
  total: number;
}

function eventContribution(
  e: StreamEvent,
  countTips: boolean,
  countCheers: boolean,
  countSuperChats: boolean,
  metric: 'coins' | 'usd',
): { name: string; value: number } | null {
  switch (e.type) {
    case 'tip':
      if (!countTips) return null;
      return { name: e.viewerName, value: metric === 'usd' ? e.coins / 100 : e.coins };
    case 'tip_received':
      if (!countTips) return null;
      return { name: e.viewerName, value: metric === 'usd' ? e.amountCents / 100 : e.amountCents };
    case 'cheer':
      if (!countCheers) return null;
      // bits as coins-equivalent (1 bit ≈ 1 coin, or $0.01)
      return { name: e.viewerName, value: metric === 'usd' ? e.bits / 100 : e.bits };
    case 'superChat':
    case 'superSticker':
      if (!countSuperChats) return null;
      return { name: e.viewerName, value: metric === 'usd' ? e.amountCents / 100 : e.amountCents };
    default:
      return null;
  }
}

export function SupportersWidget({
  fields,
  latestEvent,
}: {
  fields: Record<string, unknown>;
  latestEvent: StreamEvent | null;
}) {
  const maxSupporters = Number(fields.maxSupporters ?? 5);
  const accent = String(fields.accentColor ?? '#fbbf24');
  const title = String(fields.title ?? 'Top Supporters');
  const metric = (fields.metric as 'coins' | 'usd' | undefined) ?? 'coins';
  const countCheers = (fields.countCheers as boolean | undefined) ?? true;
  const countSuperChats = (fields.countSuperChats as boolean | undefined) ?? true;
  const countTips = (fields.countTips as boolean | undefined) ?? true;
  const showRank = (fields.showRank as boolean | undefined) ?? true;
  const compact = (fields.compact as boolean | undefined) ?? false;

  const [totals, setTotals] = useState<Map<string, number>>(new Map());
  const lastId = useRef<string | null>(null);

  useEffect(() => {
    if (!latestEvent || latestEvent.id === lastId.current) return;
    lastId.current = latestEvent.id;
    const c = eventContribution(latestEvent, countTips, countCheers, countSuperChats, metric);
    if (!c) return;
    setTotals((prev) => {
      const next = new Map(prev);
      next.set(c.name, (next.get(c.name) ?? 0) + c.value);
      return next;
    });
  }, [latestEvent, countTips, countCheers, countSuperChats, metric]);

  const sorted: Supporter[] = Array.from(totals.entries())
    .map(([name, total]) => ({ name, total }))
    .sort((a, b) => b.total - a.total)
    .slice(0, maxSupporters);

  function fmt(v: number): string {
    if (metric === 'usd') return `$${v.toFixed(2)}`;
    return `${Math.round(v).toLocaleString()} coins`;
  }

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(10,8,24,0.92), rgba(18,14,38,0.92))',
        border: `1px solid ${accent}40`,
        borderRadius: 14,
        padding: compact ? '10px 14px' : '12px 18px',
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        minWidth: 260,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 22,
          right: 22,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
          opacity: 0.6,
        }}
      />
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.5)',
          marginBottom: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span style={{ color: accent }}>★</span>
        {title}
      </div>
      {sorted.length === 0 ? (
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', padding: '8px 0' }}>
          Waiting for the first tip…
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 4 : 6 }}>
          {sorted.map((s, i) => (
            <div
              key={s.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: compact ? '2px 0' : '4px 0',
              }}
            >
              {showRank && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: i === 0 ? accent : 'rgba(255,255,255,0.35)',
                    width: 16,
                    flexShrink: 0,
                    textAlign: 'center',
                  }}
                >
                  {i + 1}
                </span>
              )}
              <span
                style={{
                  fontWeight: 700,
                  fontSize: compact ? 13 : 14,
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {s.name}
              </span>
              <span
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 700,
                  fontSize: compact ? 12 : 13,
                  color: accent,
                  flexShrink: 0,
                }}
              >
                {fmt(s.total)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
